import { useCallback, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  EyeOff,
  GripVertical,
  Plus,
  Power,
  PowerOff,
} from 'lucide-react';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';
import type { MenuConfigApi, MenuNode } from '../../hooks/useMenuConfig';
import IconGlyph from './IconGlyph';
import IconPickerModal from './IconPickerModal';
import SubItemRow from './SubItemRow';

const WINE = '#9F1239';

interface Props {
  item: MenuNode;
  index: number;
  totalItems: number;
  api: MenuConfigApi;
}

/** Draggable top-level sidebar item card (Sovereign Emerald). */
export default function MenuItemCard({ item, index, totalItems, api }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [showIconPicker, setShowIconPicker] = useState(false);

  const [{ isDragging }, drag] = useDrag({
    type: 'MENU_ITEM',
    item: () => ({ index, type: 'main' }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'MENU_ITEM',
    hover: (dragged: { index: number }) => {
      if (!ref.current || dragged.index === index) return;
      api.moveItem(dragged.index, index);
      dragged.index = index;
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node;
      drag(drop(node));
    },
    [drag, drop],
  );

  const state = api.menuStates.get(item.id) || { enabled: true, hidden: false, icon: item.icon };
  const icon = state.icon || item.icon;
  const { enabled, hidden } = state;
  const isExpanded = api.expandedItems.has(item.id);

  const availableSubs = api.allFeatures.filter(
    (f) => f.parentKey === item.id && !item.subItems?.some((sub) => sub.id === f.id),
  );
  const hasSubContent = (item.subItems?.length ?? 0) > 0 || availableSubs.length > 0;

  return (
    <>
      <div
        ref={setRef}
        className="rounded-2xl transition-shadow"
        style={{
          background: SOVEREIGN.cardBg,
          border: `1px solid ${isOver ? SOVEREIGN.brand : SOVEREIGN.cardBorder}`,
          boxShadow: isDragging ? '0 18px 44px -20px rgba(42,31,23,0.50)' : SOVEREIGN.cardShadow,
          opacity: isDragging ? 0.6 : hidden ? 0.75 : 1,
        }}
      >
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Drag handle */}
          <span
            className="cursor-move shrink-0 transition-colors"
            style={{ color: SOVEREIGN.kpiCaption }}
            title="Drag to reorder"
          >
            <GripVertical size={18} />
          </span>

          {/* Sequence stepper */}
          <span className="flex shrink-0 flex-col items-center">
            <button
              type="button"
              onClick={() => api.moveBy(item.id, -1)}
              disabled={index === 0}
              className="p-0.5 disabled:opacity-30"
              style={{ color: SOVEREIGN.bottle }}
              title="Move up"
            >
              <ChevronUp size={13} />
            </button>
            <input
              type="number"
              min={1}
              max={totalItems}
              value={index + 1}
              onChange={(e) => {
                const seq = Number.parseInt(e.target.value, 10);
                if (!Number.isNaN(seq)) api.setSequence(item.id, seq);
              }}
              className="w-9 rounded text-center text-[12px] font-extrabold tabular-nums focus:outline-none"
              style={{
                background: 'rgba(201,169,97,0.14)',
                border: '1px solid rgba(201,169,97,0.35)',
                color: '#8A5A08',
              }}
              aria-label={`Position of ${item.name}`}
            />
            <button
              type="button"
              onClick={() => api.moveBy(item.id, 1)}
              disabled={index === totalItems - 1}
              className="p-0.5 disabled:opacity-30"
              style={{ color: SOVEREIGN.bottle }}
              title="Move down"
            >
              <ChevronDown size={13} />
            </button>
          </span>

          {/* Icon tile */}
          <button
            type="button"
            onClick={() => setShowIconPicker(true)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors"
            style={{
              background: 'rgba(51,146,116,0.08)',
              border: `1px solid ${SOVEREIGN.inputBorder}`,
            }}
            title="Change icon"
          >
            <IconGlyph icon={icon} className="w-5 h-5" color={SOVEREIGN.bottle} />
          </button>

          {/* Name + path */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3
                className="truncate text-sm font-bold"
                style={{
                  color: enabled ? SOVEREIGN.title : SOVEREIGN.kpiCaption,
                  textDecoration: hidden ? 'line-through' : 'none',
                }}
              >
                {item.name}
              </h3>
              {!enabled && (
                <span
                  className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                  style={{ background: 'rgba(124,138,130,0.14)', color: SOVEREIGN.kpiCaption }}
                >
                  Disabled
                </span>
              )}
              {hidden && (
                <span
                  className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                  style={{ background: 'rgba(159,18,57,0.10)', color: WINE }}
                >
                  Hidden
                </span>
              )}
            </div>
            <p className="mt-0.5 truncate text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
              {item.path}
            </p>
          </div>

          {/* Sub-items disclosure */}
          {hasSubContent && (
            <button
              type="button"
              onClick={() => api.toggleExpand(item.id)}
              className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold transition-colors"
              style={{
                background: isExpanded ? 'rgba(31,90,71,0.08)' : SOVEREIGN.inputBg,
                border: `1px solid ${SOVEREIGN.inputBorder}`,
                color: SOVEREIGN.bottle,
              }}
              aria-expanded={isExpanded}
            >
              {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              {item.subItems?.length ?? 0} sub
            </button>
          )}

          {/* Toggles */}
          <span className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => api.toggleEnabled(item.id)}
              className="rounded-lg p-2 transition-colors"
              style={{
                background: enabled ? 'rgba(51,146,116,0.10)' : 'rgba(124,138,130,0.12)',
                color: enabled ? SOVEREIGN.bottle : SOVEREIGN.kpiCaption,
                border: `1px solid ${enabled ? 'rgba(51,146,116,0.25)' : SOVEREIGN.barTrackBorder}`,
              }}
              title={enabled ? 'Disable menu' : 'Enable menu'}
            >
              {enabled ? <Power size={14} /> : <PowerOff size={14} />}
            </button>
            <button
              type="button"
              onClick={() => api.toggleHidden(item.id)}
              className="rounded-lg p-2 transition-colors"
              style={{
                background: hidden ? 'rgba(159,18,57,0.08)' : 'rgba(201,169,97,0.12)',
                color: hidden ? WINE : '#8A5A08',
                border: `1px solid ${hidden ? 'rgba(159,18,57,0.22)' : 'rgba(201,169,97,0.35)'}`,
              }}
              title={hidden ? 'Show in sidebar' : 'Hide from sidebar'}
            >
              {hidden ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </span>
        </div>

        {/* Expanded sub-items */}
        {isExpanded && hasSubContent && (
          <div
            className="space-y-1.5 px-4 pb-3 pt-3"
            style={{ borderTop: `1px dashed ${SOVEREIGN.innerRule}` }}
          >
            {availableSubs.length > 0 && (
              <div className="mb-2">
                <p
                  className="mb-1.5 text-[9.5px] font-bold uppercase tracking-[0.12em]"
                  style={{ color: SOVEREIGN.kpiCaption }}
                >
                  Available submenus
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {availableSubs.map((subItem) => (
                    <button
                      key={subItem.id}
                      type="button"
                      onClick={() => api.addSubItem(item.id, subItem)}
                      className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors"
                      style={{
                        background: SOVEREIGN.inputBg,
                        border: `1px solid ${SOVEREIGN.inputBorder}`,
                        color: SOVEREIGN.bottle,
                      }}
                    >
                      <Plus size={11} />
                      {subItem.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(item.subItems?.length ?? 0) > 0 ? (
              item.subItems?.map((subItem, subIndex) => (
                <SubItemRow
                  key={subItem.id}
                  item={subItem}
                  parentId={item.id}
                  parentIndex={index}
                  index={subIndex}
                  totalSubItems={item.subItems?.length ?? 0}
                  state={
                    api.subMenuStates.get(item.id)?.get(subItem.id) || {
                      enabled: true,
                      hidden: false,
                      icon: subItem.icon,
                    }
                  }
                  moveSubItem={api.moveSubItem}
                  onToggleEnabled={() => api.toggleSubEnabled(item.id, subItem.id)}
                  onToggleHidden={() => api.toggleSubHidden(item.id, subItem.id)}
                  onIconChange={(icon) => api.setSubIcon(item.id, subItem.id, icon)}
                  onSetSequence={api.setSubSequence}
                  onMoveBy={api.moveSubBy}
                />
              ))
            ) : (
              <p className="flex items-center gap-1.5 text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
                <CheckCircle size={12} />
                No submenus added yet — pick one above.
              </p>
            )}
          </div>
        )}
      </div>

      {showIconPicker && (
        <IconPickerModal
          currentIcon={icon}
          onSelect={(icon) => api.setIcon(item.id, icon)}
          onClose={() => setShowIconPicker(false)}
        />
      )}
    </>
  );
}
